import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import {
	performDeleteRequest,
	performGetRequest,
	performPutRequest,
	isHttpClientErrorResponse,
} from '@atlassian/jira-fetch';
import { IS_CONNECTED_JSW_PROJECT_MIGRATED_KEY, PROJECT_PROPERTY_KEY } from './constants';

export type ProjectPropertiesApiResponse<T> = {
	key: string;
	value: T | null;
};

const getUrl = (projectKeyOrId: string, propertyKey: string) =>
	`/rest/api/3/project/${projectKeyOrId}/properties/${propertyKey}`;

const handleErrorLogging = (
	operation: string,
	error: Error,
	attributes: Record<string, string>,
) => {
	fireErrorAnalytics({
		meta: {
			id: operation,
			packageName: 'jiraBusinessCalendar',
			teamName: 'wanjel',
		},
		error,
		sendToPrivacyUnsafeSplunk: true,
		attributes,
	});
};

const createProjectPropertySetter =
	(PROPERTY_KEY: string) =>
	async <T,>({
		propertyValue,
		projectKeyOrId,
	}: {
		propertyValue: T;
		projectKeyOrId: string;
	}): Promise<void> => {
		const url = getUrl(projectKeyOrId, PROPERTY_KEY);

		try {
			await performPutRequest(url, {
				body: JSON.stringify(propertyValue),
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			handleErrorLogging('setProjectProperty', error, {
				projectKeyOrId,
				propertyValue: JSON.stringify(propertyValue),
			});
			throw error;
		}
	};

export const setProjectProperty = createProjectPropertySetter(PROJECT_PROPERTY_KEY);

const createProjectPropertyGetter =
	(PROPERTY_KEY: string) =>
	async <T,>({
		projectKeyOrId,
	}: {
		projectKeyOrId: string;
	}): Promise<ProjectPropertiesApiResponse<T>> => {
		const url = getUrl(projectKeyOrId, PROPERTY_KEY);

		try {
			const response: ProjectPropertiesApiResponse<T> = await performGetRequest(url);

			return response;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			//  The endpoint returns 404 if the project property is not set, we don't treat it as an actual error
			if (error.statusCode === 404) {
				return { key: PROPERTY_KEY, value: null };
			}
			handleErrorLogging('getProjectProperty', error, {
				projectKeyOrId,
			});
			throw error;
		}
	};

export const getProjectProperty = createProjectPropertyGetter(PROJECT_PROPERTY_KEY);

export const deleteProjectProperty = async ({
	projectKeyOrId,
}: {
	projectKeyOrId: string;
}): Promise<void> => {
	const url = getUrl(projectKeyOrId, PROJECT_PROPERTY_KEY);

	try {
		await performDeleteRequest(url);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (!isHttpClientErrorResponse(error)) {
			handleErrorLogging('deleteProjectProperty', error, {
				projectKeyOrId,
			});
		}

		throw error;
	}
};

// TODO WOLVES-576 cleanup after migration
export const getIsProjectMigratedProperty = createProjectPropertyGetter(
	IS_CONNECTED_JSW_PROJECT_MIGRATED_KEY,
);

// TODO WOLVES-576 cleanup after migration
export const setIsProjectMigratedProperty = createProjectPropertySetter(
	IS_CONNECTED_JSW_PROJECT_MIGRATED_KEY,
);
